// ---------------------------------------------------------------------------
// TYPES
// ---------------------------------------------------------------------------

export type ReportTypeOption = {
  readonly value: string
  readonly label: string
}

export type ActionType =
  | 'device_transfer'
  | 'crew_composition'
  | 'transfer_crew'
  | 'change_composition'
  | 'out_of_service'
  | 'set_on_mission'
  | 'back_to_service'
  | 'back_to_available'
  | 'last_dispatches'
  | 'disconnect_device'
  | 'connect_device'
  | 'activate_device'
  | 'activate_device_start'
  | 'create'
  | 'edit'
  | 'edit_identification'

// ---------------------------------------------------------------------------
// CONSTANTS (DEFINITIONS)
// ---------------------------------------------------------------------------

const DEVICE_ACTION_ROUTES: Record<string, string | readonly string[]> = {
  activate_device: '/activate-deactivate',
  activate_device_start: '/device/',
  back_to_available: '/device/back-to-available',
  back_to_service: '/device/back-to-service',
  change_composition: '/device/logs/change-composition',
  connect_device: ['/auth/connect-mobile', '/auth/operator/login/device'],
  create: '/device/store', // POST
  crew_composition: '/device/logs/crew-composition',
  device_transfer: '/device/transfer',
  disconnect_device: ['/logout/device', '/auth/logout/device/external'],
  edit: '/device/', // PUT
  edit_identification: '/change-identifications',
  last_dispatches: '/device/last-dispatches',
  out_of_service: '/device/out-of-service',
  set_on_mission: '/device/set-on-mission',
  transfer_crew: '/device/logs/transfer-crew',
}

// Mantém as descrições originais (serão traduzidas)
const DEVICE_ACTION_LABELS: Record<string, string> = {
  activate_device: 'Activate/Deactivate device',
  back_to_available: 'Back to available',
  back_to_service: 'Back to service',
  change_composition: 'Edit crew',
  connect_device: 'Connect',
  create: 'Create device',
  crew_composition: 'See Crew',
  device_transfer: 'Transfer',
  disconnect_device: 'Disconnect',
  edit: 'Edit device',
  edit_identification: 'Edit IMEI/Tracking',
  last_dispatches: 'Chronology',
  out_of_service: 'Not in Service',
  set_on_mission: 'Set on mission',
  transfer_crew: 'Replace device',
}

// ---------------------------------------------------------------------------
// INTERNAL HELPERS
// ---------------------------------------------------------------------------

const normalizeUrl = (input: string) =>
  String(input)
    .split(/[?#]/)[0]
    .replace(/(^\/+|\/+$)/g, '')
    .replace(/%20/g, ' ')

const translateAllLabels = (translate: (item: string) => string) =>
  Object.fromEntries(
    Object.entries(DEVICE_ACTION_LABELS).map(([key, label]) => [key, translate(label)])
  ) as Record<string, string>

// Combina prefix + label evitando duplicação quando label já inicia com o prefixo
function getTranslatedLabel(key: string, translate: (item: string) => string) {
  const labels = translateAllLabels(translate)
  const label = String(labels[key] ?? '').trim()
  if (!label) return undefined

  return `${label}`
}

// ---------------------------------------------------------------------------
// EXPORTED FUNCTIONS
// ---------------------------------------------------------------------------

export function getDeviceActionOptions(
  translate: (item: string) => string
): readonly ReportTypeOption[] {
  const map = getDeviceActionToLabelMap(translate)
  return (Object.keys(map) as readonly ActionType[]).map((actionKey) => ({
    value: actionKey,
    label: map[actionKey],
  }))
}

export function getDeviceRouteOptions(
  translate: (item: string) => string
): readonly ReportTypeOption[] {
  return Object.keys(DEVICE_ACTION_ROUTES).map((routeKey) => {
    const value = DEVICE_ACTION_ROUTES[routeKey]
    const primaryRoute = Array.isArray(value) ? value[0] : value

    return {
      value: primaryRoute,
      label: getTranslatedLabel(routeKey, translate) ?? translate(String(routeKey)),
    }
  }) as readonly ReportTypeOption[]
}

//  Cria um mapa de { ActionType: LabelTraduzida }.

export function getDeviceActionToLabelMap(
  translate: (item: string) => string
): Record<ActionType, string> {
  const keys = Object.keys(DEVICE_ACTION_LABELS) as readonly string[]
  const map = keys.reduce<Record<string, string>>((acc, actionKey) => {
    const combined = getTranslatedLabel(actionKey, translate) ?? translate(String(actionKey))
    return { ...acc, [actionKey]: combined }
  }, {})
  return map as Record<ActionType, string>
}

export function getDeviceRouteToLabelMap(
  translate: (item: string) => string
): Record<string, string> {
  return (Object.keys(DEVICE_ACTION_ROUTES) as readonly string[]).reduce<
    Record<string, string>
  >((acc, routeKey) => {
    const entry = DEVICE_ACTION_ROUTES[routeKey]
    const label = getTranslatedLabel(routeKey, translate) ?? translate(String(routeKey))
    const values = Array.isArray(entry) ? entry : [entry]

    // Adiciona mapeamentos para cada variação da rota
    const next = values.reduce((accInner, route) => {
      const normalizedRoutePath = normalizeUrl(route)
      return {
        ...accInner,
        [route]: accInner[route] ?? label,
        ['/' + normalizedRoutePath]: accInner['/' + normalizedRoutePath] ?? label,
        [normalizedRoutePath]: accInner[normalizedRoutePath] ?? label,
      }
    }, {} as Record<string, string>)

    return { ...acc, ...next }
  }, {})
}


export function findDeviceLabelByUrl(
  route: string,
  translate: (item: string) => string
): string | undefined {
  if (!route) return undefined
  const incoming = normalizeUrl(route)
  if (!incoming) return undefined

  const targets = Object.entries(DEVICE_ACTION_ROUTES).flatMap(([key, routePath]) => {
    const values = Array.isArray(routePath) ? routePath : [routePath]
    return values.map((pathValue) => ({
      key,
      target: normalizeUrl(pathValue),
      last: normalizeUrl(pathValue).split('/').filter(Boolean).pop() ?? '',
    }))
  })

  // 1. Tentativa exata
  const exact = targets.find((targetEntry) => targetEntry.target === incoming)
  if (exact) return getTranslatedLabel(exact.key, translate)

  // 2. Tentativa por sufixo — prioriza rotas mais específicas que aparecem no final da URL
  const bySuffix = targets.find(
    (targetEntry) =>
      targetEntry.target &&
      (incoming === targetEntry.target || incoming.endsWith('/' + targetEntry.target) || incoming.endsWith(targetEntry.target))
  )
  if (bySuffix) return getTranslatedLabel(bySuffix.key, translate)

  // 3. Tentativa por prefixo (ex.: device/3015)
  const byPrefixCandidates = targets.filter((targetEntry) =>
    targetEntry.target && (incoming === targetEntry.target || incoming.startsWith(targetEntry.target + '/'))
  )
  if (byPrefixCandidates.length > 0) {

    const preferred = byPrefixCandidates.sort((a, b) => (b.target.length ?? 0) - (a.target.length ?? 0))[0]
    return getTranslatedLabel(preferred.key, translate)
  }

  // 4. Tentativa pelo último segmento da URL
  const incomingLast = incoming.split('/').filter(Boolean).pop() ?? ''
  const byLast = targets.find((targetEntry) => targetEntry.last && targetEntry.last === incomingLast)
  if (byLast) return getTranslatedLabel(byLast.key, translate)

  return undefined
}
